/**
 * Endpoint surface for `/panel-api/v1`, grouped the way docs/panel-api.md is.
 * Every function returns component-ready data (mapped) or throws `ApiError`.
 */
import { buildQuery, createClient, DEFAULT_BASE_URL } from "./client.js";
import {
  mapAdminAudit,
  mapAdminCustomerPage,
  mapAdminMetrics,
  mapBilling,
  mapChannel,
  mapChannelPage,
  mapContactSubmission,
  mapDashboard,
  mapDeliveryPage,
  mapDowntimeList,
  mapDowntimeWindow,
  mapFeedContent,
  mapFeedDetail,
  mapFeedSchedule,
  mapNotificationRows,
  mapPlanChange,
  mapPollLog,
  mapPlan,
  mapPortal,
  mapProject,
  mapProjectActivity,
  mapProjectDetail,
  mapAlertSettings,
  mapReliabilityReport,
  toProjectBody,
  toProjectPatch,
  toAlertRuleBody,
} from "./mappers.js";

export const PANEL_API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_PANEL_API_BASE) || DEFAULT_BASE_URL;

export function createPanelApi(client) {
  return {
    /* client controls, kept on the same object the screens receive */
    baseUrl: client.baseUrl,
    url: client.url,
    setCsrfToken: client.setCsrfToken,
    getCsrfToken: client.getCsrfToken,
    setUnauthorizedHandler: client.setUnauthorizedHandler,

    auth: {
      session: () => client.get("/auth/session"),
      // Public: what this deployment offers an anonymous visitor. Today that is
      // the human check's site key, which the signup screen needs before it can
      // render the widget.
      config: () => client.get("/auth/config"),
      signup: (body) => client.post("/auth/signup", body),
      login: (body) => client.post("/auth/login", body),
      // `purpose` rides along: the same endpoint completes an address check, a
      // login second factor and a phone check, and the code store namespaces
      // codes by purpose.
      verifyTwoFactor: (body) => client.post("/auth/2fa/verify", body),
      resendTwoFactor: (body) => client.post("/auth/2fa/resend", body),
      logout: () => client.post("/auth/logout"),
      // Always answers 202 — never leaks whether the account exists.
      requestPasswordReset: (email) => client.post("/auth/password/reset", { email }),
      confirmPasswordReset: ({ token, password }) =>
        client.post("/auth/password/reset/confirm", { token, password }),
      startOAuth: (provider) => client.startOAuth(provider),
      oauthUrl: (provider) => client.url(`/auth/oauth/${encodeURIComponent(provider)}/start`),
    },

    dashboard: () => client.get("/dashboard").then(mapDashboard),

    me: {
      get: () => client.get("/me"),
      notifications: () => client.get("/me/notifications").then(mapNotificationRows),
      saveNotifications: (rows) =>
        client
          .put("/me/notifications", {
            rows: rows.map((row) => ({ key: row.key, email: Boolean(row.email), sms: Boolean(row.sms) })),
          })
          .then(mapNotificationRows),
      // Method chooses the factor: "totp" (an authenticator app, the default) or
      // "sms", which needs a phone number and is confirmed with a delivered code.
      enableTwoFactor: ({ method = "totp", phone } = {}) =>
        client.post("/me/2fa/enable", { method, ...(phone ? { phone } : {}) }),
      confirmTwoFactor: (code) => client.post("/me/2fa/confirm", { code }),
      // An SMS factor needs the code that turning it off requires; a TOTP factor
      // generates its own, and this call answers 400 for it.
      requestDisableCode: () => client.post("/me/2fa/disable/code"),
      disableTwoFactor: (code) => client.post("/me/2fa/disable", { code }),
      changePassword: ({ currentPassword, newPassword }) =>
        client.post("/me/password", { current_password: currentPassword, new_password: newPassword }),
      // An IANA name, not an offset: an offset picked in July is an hour wrong
      // from November, and the panel prints instants all year.
      updateTimezone: (timezone) => client.patch("/me/timezone", { timezone }),
    },

    projects: {
      list: () => client.get("/projects").then((data) => (data?.projects ?? []).map(mapProject)),
      create: (input) =>
        client.post("/projects", toProjectBody(input)).then((data) => mapProject(data?.project)),
      get: (projectId) =>
        client.get(`/projects/${encodeURIComponent(projectId)}`).then(mapProjectDetail),
      // A true partial update: only the keys the caller passed are sent, so
      // saving one field cannot reset the others (see toProjectPatch).
      update: (projectId, patch) =>
        client
          .patch(`/projects/${encodeURIComponent(projectId)}`, toProjectPatch(patch))
          .then((data) => mapProject(data?.project)),
      remove: (projectId) => client.del(`/projects/${encodeURIComponent(projectId)}`),

      // The project's last day of traffic and changes, hour by hour.
      activity: (projectId, { hours = 24 } = {}) =>
        client
          .get(`/projects/${encodeURIComponent(projectId)}/activity${buildQuery({ hours })}`)
          .then(mapProjectActivity),

      // The failure share of this project's polling, at three levels: everything,
      // by provider, by feed. One request, because the three are the same
      // measurement grouped differently and asking three times could show three
      // different windows.
      reliability: (projectId, { window = "24h" } = {}) =>
        client
          .get(`/projects/${encodeURIComponent(projectId)}/reliability${buildQuery({ window })}`)
          .then(mapReliabilityReport),

      // The failure-rate thresholds: a flat list, set one rule at a time. A
      // whole-object PUT would make every screen that edits one scope a way to
      // wipe the others.
      alertRules: (projectId) =>
        client
          .get(`/projects/${encodeURIComponent(projectId)}/alerts`)
          .then(mapAlertSettings),
      saveAlertRule: (projectId, rule) =>
        client
          .put(`/projects/${encodeURIComponent(projectId)}/alerts`, toAlertRuleBody(rule))
          .then(mapAlertSettings),
      deleteAlertRule: (projectId, { scope }) =>
        client.del(`/projects/${encodeURIComponent(projectId)}/alerts${buildQuery({ scope })}`),

      // `sort` and `order` are decided by the server, because the page is cut
      // there: ordering the rows that came back would sort one page out of many.
      // `provider` is part of the query, not a filter applied to the rows that
      // come back: the list is a page cut inside the database, so sifting the
      // fifty rows on screen would answer "which of these are Smoobu" and the
      // total under the list would count rows the customer cannot see.
      channels: (projectId, { q, provider, limit = 50, offset = 0, sort, order } = {}) =>
        client
          .get(
            `/projects/${encodeURIComponent(projectId)}/channels${buildQuery({ q, provider, limit, offset, sort, order })}`,
          )
          .then(mapChannelPage),
      addChannel: (projectId, { url, externalId }) =>
        client
          .post(`/projects/${encodeURIComponent(projectId)}/channels`, {
            url,
            external_id: externalId,
          })
          .then((data) => mapChannel(data?.channel)),
      // The response reports what the deletion collected: whether the calendar
      // behind the channel was removed too, or whether another project still
      // subscribes to it.
      removeChannel: (projectId, externalId) =>
        client
          .del(
            `/projects/${encodeURIComponent(projectId)}/channels/${encodeURIComponent(externalId)}`,
          )
          .then((data) => ({
            deleted: Boolean(data?.deleted),
            externalId: data?.external_id ?? externalId,
            cleanup: {
              feedId: data?.cleanup?.feed_id ?? null,
              feedRemoved: Boolean(data?.cleanup?.feed_removed),
              artifactsPurged: Boolean(data?.cleanup?.artifacts_purged),
            },
          })),

      deliveries: (projectId, { status = "all", limit = 50, offset = 0 } = {}) =>
        client
          .get(
            `/projects/${encodeURIComponent(projectId)}/deliveries${buildQuery({
              status,
              limit,
              offset,
            })}`,
          )
          .then(mapDeliveryPage),

      downtime: (projectId) =>
        client
          .get(`/projects/${encodeURIComponent(projectId)}/downtime`)
          .then(mapDowntimeList),
      addDowntime: (projectId, { startsAt, endsAt, label }) =>
        client
          .post(`/projects/${encodeURIComponent(projectId)}/downtime`, {
            starts_at: startsAt,
            ends_at: endsAt,
            label,
          })
          .then((data) => mapDowntimeWindow(data?.window)),
      removeDowntime: (projectId, windowId) =>
        client.del(
          `/projects/${encodeURIComponent(projectId)}/downtime/${encodeURIComponent(windowId)}`,
        ),

      testWebhook: (projectId) =>
        client.post(`/projects/${encodeURIComponent(projectId)}/webhook/test`),
      revealSecret: (projectId, code) =>
        client.post(`/projects/${encodeURIComponent(projectId)}/secret/reveal`, { code }),
    },

    feeds: {
      get: (feedId) => client.get(`/feeds/${encodeURIComponent(feedId)}`).then(mapFeedDetail),
      // The upcoming poll times of a fixed-interval feed; an adaptive feed
      // answers with mode "adaptive" and no list.
      schedule: (feedId, { count = 10 } = {}) =>
        client
          .get(`/feeds/${encodeURIComponent(feedId)}/schedule${buildQuery({ count })}`)
          .then(mapFeedSchedule),
      // What actually went out, newest first. Short-lived: Redis, not the DB.
      polls: (feedId, { limit = 50 } = {}) =>
        client
          .get(`/feeds/${encodeURIComponent(feedId)}/polls${buildQuery({ limit })}`)
          .then(mapPollLog),
      // The cached calendar behind content_mode, plus the events that
      // disappeared from it. A development aid: the deployment answers 403
      // feature_disabled unless PANEL_FEED_CONTENT_INSPECTION is on.
      content: (feedId, { removals = 50 } = {}) =>
        client
          .get(`/feeds/${encodeURIComponent(feedId)}/content${buildQuery({ removals })}`)
          .then(mapFeedContent),
    },

    billing: {
      get: () => client.get("/billing").then(mapBilling),
      // Either a local switch (the free tier) or a hosted checkout to send the
      // browser to; `mapPlanChange` keeps the two apart so the screen cannot
      // report a plan change that has not been paid for.
      //
      // `interval` picks which of the plan's prices is bought — "monthly" or
      // "yearly". Leaving it out buys a month, which is what the free-tier switch
      // and any caller that predates yearly billing want.
      changePlan: (planId, interval) =>
        client.post("/billing/plan", { plan_id: planId, interval }).then(mapPlanChange),
      // 503 not_configured while there is no PSP, otherwise a portal URL.
      updatePayment: () => client.post("/billing/payment").then(mapPortal),
      downloadInvoice: (invoiceId) =>
        client.download(`/billing/invoices/${encodeURIComponent(invoiceId)}`),
    },

    // The help form. There is no reply address in the body and none in the
    // signature: the server reads it from the session, and the only recipient is
    // the deployment's support mailbox. See docs/panel-api.md.
    support: {
      contact: ({ subject, body }) =>
        client.post("/support/contact", { subject, body }).then(mapContactSubmission),
    },

    admin: {
      metrics: () => client.get("/admin/metrics").then(mapAdminMetrics),
      // The same report across every customer, with no project filter. `projects`
      // on each row is how many accounts watch that calendar.
      reliability: ({ window = "24h" } = {}) =>
        client.get(`/admin/reliability${buildQuery({ window })}`).then(mapReliabilityReport),
      customers: (q, { limit = 50, offset = 0 } = {}) =>
        client
          .get(`/admin/customers${buildQuery({ q, limit, offset })}`)
          .then(mapAdminCustomerPage),
      customer: (customerId) =>
        client.get(`/admin/customers/${encodeURIComponent(customerId)}`),
      suspend: (customerId) =>
        client.post(`/admin/customers/${encodeURIComponent(customerId)}/suspend`),
      resume: (customerId) =>
        client.post(`/admin/customers/${encodeURIComponent(customerId)}/resume`),
      // Removes the account and everything that hangs off it. The server refuses
      // to delete the signed-in account or another administrator.
      remove: (customerId) =>
        client.del(`/admin/customers/${encodeURIComponent(customerId)}`),
      audit: (limit = 50) =>
        client.get(`/admin/audit${buildQuery({ limit })}`).then(mapAdminAudit),
    },
  };
}

/** App-wide singleton. Tests build their own via `createPanelApi(createClient(...))`. */
export const panelApi = createPanelApi(createClient({ baseUrl: PANEL_API_BASE }));

export { ApiError, createClient } from "./client.js";
export * as mappers from "./mappers.js";
